import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { Heart, HandHeart, Sparkles, CheckCircle2, Building2, GraduationCap, Users } from "lucide-react";

const DONORS = [
  {
    name: "Michael & Susan Dell Foundation",
    tag: "Founding Partner",
    desc: "A national leader in student success and college-to-career pathways, whose commitment to durable, workforce-aligned skills helped make GPA+ possible.",
    featured: true,
  },
  { name: "Your organization here", tag: "Program Sponsor", placeholder: true },
  { name: "Your organization here", tag: "Community Supporter", placeholder: true },
];

const IMPACT = [
  { icon: GraduationCap, stat: "10", label: "durable skills measured" },
  { icon: Users, stat: "4", label: "audiences served" },
  { icon: Sparkles, stat: "18", label: "career pathways mapped" },
];

const TIERS = ["Founding Partner", "Program Sponsor", "Community Supporter", "In-kind / Other"];

export default function Supporters() {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", tier: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      toast.error("Please fill in your company, name, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsors", { ...form, ref: localStorage.getItem("gpaplus_ref") || undefined });
      setDone(true);
      toast.success("Thank you! Your interest has been received.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingPage
      eyebrow="Supporters & Partners"
      title="Built with the support of those who believe in student potential."
      lead="GPA+ exists because forward-thinking donors and partners share our conviction that a transcript alone can't capture what a student can do. Their support turns durable-skills research into a working tool for students, educators, and employers."
    >
      {/* Impact strip */}
      <div className="grid grid-cols-3 gap-4">
        {IMPACT.map((i) => (
          <div key={i.label} className="rounded-2xl border border-slate-200 bg-white p-5 text-center card-elevate">
            <i.icon className="mx-auto h-6 w-6 text-[#78be21]" />
            <p className="mt-2 font-mono-num text-3xl font-bold text-[#003057]">{i.stat}</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{i.label}</p>
          </div>
        ))}
      </div>

      {/* Donors */}
      <div className="mt-16 flex items-center gap-3">
        <Heart className="h-6 w-6 text-[#78be21]" />
        <h2 className="font-heading text-2xl font-bold text-[#003057]">Our supporters</h2>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
        {DONORS.map((d, i) => (
          <div
            key={i}
            data-testid="donor-card"
            className={`rounded-2xl border p-6 ${
              d.featured
                ? "border-[#78be21] bg-white shadow-lg card-elevate md:col-span-3"
                : "border-dashed border-slate-300 bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`flex h-14 w-14 items-center justify-center rounded-xl ${d.placeholder ? "bg-slate-200" : "bg-[#003057]"}`}>
                <Building2 className={`h-6 w-6 ${d.placeholder ? "text-slate-400" : "text-[#78be21]"}`} />
              </div>
              <div>
                <span className="rounded-full bg-[#78be21]/15 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest text-[#5fa114]">
                  {d.tag}
                </span>
                <h3 className={`mt-1.5 font-heading text-lg font-bold ${d.placeholder ? "text-slate-400" : "text-[#003057]"}`}>
                  {d.name}
                </h3>
              </div>
            </div>
            {d.desc && <p className="mt-4 text-sm leading-relaxed text-slate-600">{d.desc}</p>}
            {d.placeholder && <p className="mt-4 text-sm italic text-slate-400">Become a partner and see your organization featured here.</p>}
          </div>
        ))}
      </div>

      {/* Become a sponsor */}
      <div id="become-a-sponsor" className="mt-16 grid grid-cols-1 gap-8 rounded-2xl bg-[#003057] p-8 text-white lg:grid-cols-2 lg:p-10">
        <div>
          <div className="flex items-center gap-3">
            <HandHeart className="h-7 w-7 text-[#78be21]" />
            <h2 className="font-heading text-2xl font-bold">Become a supporter</h2>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Partner with Lone Star College to help every student prove the durable skills that careers demand.
            Sponsorship fuels the Learning Academy, expands career pathways, and keeps GPA+ free for students.
            Tell us a little about your organization and we'll be in touch.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-200">
            {["Be featured as an official GPA+ supporter", "Directly fund student skill-building", "Shape the future of skills-first hiring"].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#78be21]" /> {b}
              </li>
            ))}
          </ul>
        </div>

        {done ? (
          <div className="flex flex-col items-center justify-center rounded-2xl bg-white/5 p-8 text-center" data-testid="sponsor-success">
            <CheckCircle2 className="h-12 w-12 text-[#78be21]" />
            <h3 className="mt-4 font-heading text-xl font-bold">Thank you!</h3>
            <p className="mt-2 text-sm text-slate-300">
              Your interest has been received. Our team will reach out to {form.email} shortly.
            </p>
            <Button
              data-testid="sponsor-another"
              onClick={() => { setDone(false); setForm({ company: "", contact_name: "", email: "", phone: "", tier: "", message: "" }); }}
              className="mt-6 rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]"
            >
              Submit another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white p-6 text-slate-800" data-testid="sponsor-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="company" className="text-xs font-bold uppercase tracking-wide text-slate-500">Organization *</Label>
                <Input id="company" data-testid="sponsor-company" value={form.company} onChange={set("company")} placeholder="Acme Foundation" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="contact" className="text-xs font-bold uppercase tracking-wide text-slate-500">Contact name *</Label>
                <Input id="contact" data-testid="sponsor-contact" value={form.contact_name} onChange={set("contact_name")} placeholder="Jane Doe" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide text-slate-500">Email *</Label>
                <Input id="email" data-testid="sponsor-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@acme.org" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wide text-slate-500">Phone</Label>
                <Input id="phone" data-testid="sponsor-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Support level</Label>
                <Select value={form.tier} onValueChange={(v) => setForm((f) => ({ ...f, tier: v }))}>
                  <SelectTrigger data-testid="sponsor-tier" className="mt-1"><SelectValue placeholder="Choose a level" /></SelectTrigger>
                  <SelectContent>
                    {TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wide text-slate-500">Message</Label>
                <Textarea id="message" data-testid="sponsor-message" value={form.message} onChange={set("message")} placeholder="Tell us how you'd like to support GPA+…" className="mt-1" rows={3} />
              </div>
            </div>
            <Button type="submit" disabled={submitting} data-testid="sponsor-submit"
              className="mt-5 w-full rounded-full bg-[#78be21] py-6 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
              {submitting ? "Sending…" : "Submit interest"}
            </Button>
          </form>
        )}
      </div>

      <div className="mt-10 text-center text-sm text-slate-500">
        Learn how we safeguard student data on our{" "}
        <Link to="/privacy" className="font-semibold text-[#5fa114] hover:underline">Privacy & Compliance</Link> page.
      </div>
    </MarketingPage>
  );
}
